import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import RouteService from "../../../services/inspection-overview/site-operations/route.services";
import Helpers from "../../../utils/helpers";

type RouteInformationPayload = {
  cacheKey: string | null;
  data: unknown;
};

type RouteInformationInflight = Promise<RouteInformationPayload>;

const routeInformationInflight = new Map<string, RouteInformationInflight>();

function normalizeRouteInformationPayload(raw: unknown) {
  if (raw == null) {
    return [];
  }
  if (Array.isArray(raw)) {
    return raw;
  }
  return raw;
}

/**
 * Loads route information for the given site. Skips the network when the same
 * cameraSiteId is already cached in Redux. Dedupes concurrent in-flight requests.
 */
export const ensureRouteInformation = createAsyncThunk(
  "RouteDetails/ensureRouteInformation",
  async (
    cameraSiteId: string | null | undefined,
    { getState }
  ): Promise<RouteInformationPayload> => {
    const id =
      cameraSiteId != null && cameraSiteId !== ""
        ? String(cameraSiteId)
        : null;

    if (!id) {
      return { cacheKey: null, data: [] };
    }

    const routeState = (
      getState() as {
        RouteDetails: {
          routeInformationCacheKey: string | null;
          routeInfo: unknown;
        };
      }
    ).RouteDetails;

    if (routeState.routeInformationCacheKey === id) {
      return { cacheKey: id, data: routeState.routeInfo };
    }

    let inflight = routeInformationInflight.get(id);
    if (!inflight) {
      inflight = (async (): Promise<RouteInformationPayload> => {
        try {
          const raw = await RouteService.getRouteInformation(id);
          const data = normalizeRouteInformationPayload(raw);
          return { cacheKey: id, data };
        } catch (error) {
          Helpers.ConsoleLogError(error);
          return { cacheKey: id, data: [] };
        } finally {
          routeInformationInflight.delete(id);
        }
      })();
      routeInformationInflight.set(id, inflight);
    }

    return inflight;
  }
);

const RouteDetails = createSlice({
  name: "RouteDetails",
  initialState: {
    routeInfo: {} as unknown,
    routeInformationCacheKey: null as string | null,
    routeSummary: {},
    selectedRouteInfo: {},
  },
  reducers: {
    RouteInfoReducer: (state: any, action: any) => {
      state.routeInfo = action.payload;
      state.routeInformationCacheKey = null;
      return state;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(ensureRouteInformation.fulfilled, (state, action) => {
      state.routeInformationCacheKey = action.payload.cacheKey;
      state.routeInfo = action.payload.data;
    });
  },
});

export const { 
    RouteInfoReducer,
} = RouteDetails.actions;

export default RouteDetails;
